// Version 1 — Summary view.
//
// Combines two dollar-impact streams into one Y1 / Y2 / Y3 picture:
//
//   1. Hub-shift savings ($)
//      For each (discipline × channel × level) cell:
//        baseline = adjustedFte × cost(onshore)
//        modeled  = onFte × cost(onshore)
//                 + nearFte × cost(nearshore)
//                 + offFte × cost(offshore)
//      hub-shift = baseline − modeled. Treated as steady-state across years
//      since the engine models the shore split structurally, not as a ramp.
//
//   2. Automation savings ($)
//      Released hours per cell come from the engine's automation stage, so this
//      page cannot disagree with the Automation Impact tab. Hours × cell-blended
//      $/hr gives full-impact dollars; the horizon scales them by each
//      automation's own ramp.
//
// Heatmap rows are (discipline × channel) pairs; columns are level groups. Cell
// shading scales with the combined dollar impact for the active horizon.
//
// The page also renders post-automation headcount at the active horizon, and a
// Final Staff Plan card that freezes it — matrix plus the scenario inputs that
// produced it — into the client workspace as its own kind of plan.
//
// Cross-component shared math (lookupCostPerRole, FTE_CAPACITY, the automation
// stage) is imported from @rc/engine so the numbers reconcile with the Cost
// Analysis, Hub Comparison, Automation Impact, and Phase Concentration cards.
// The shore split itself comes from the engine's per-cell cost (`totalCost`),
// which reflects the scenario's OGS config.

import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { BarChart3, Coins, Flag, Sparkles } from 'lucide-react';
import {
  automationAdoption,
  automationCellImpacts,
  automationHorizonLabel,
  automationHorizonWords,
  automationReleaseByCell,
  automationTotals,
  basisCellsToStaffPlanCells,
  currencySymbol,
  FTE_CAPACITY,
  LEVEL_GROUPS,
  levelGroupOf,
  lookupCostPerRole,
  resolveAutomationDeployments,
  scenarioAutomations,
  staffPlanCellKey,
  staffPlanHorizonFor,
  type AutomationCellRelease,
  type AutomationHorizon,
  type LevelGroup,
  type LevelGroupId,
} from '@rc/engine';
import {
  bgFor as bgForPalette,
  txtFor as txtForPalette,
  HEATMAP_EMERALD,
} from '../../lib/heatmap';
import type {
  AutomationBasis,
  CalculationResult,
  ClientProfile,
  DisciplineChannelCell,
  LevelId,
  Model,
  ScenarioWorkspace,
  StaffPlan,
  StaffPlanSnapshotAutomation,
} from '@rc/engine/types';
import {
  availableBases,
  basisCells as cellsForBasis,
  basisLabel,
  resolveBasis,
} from './automationBasis';
import {
  activeColumnsFor,
  buildGroups,
  columnSplit,
  GROUP_COLUMNS,
  type LevelSplit,
  type MatrixColumn,
} from './staffMatrix';
import ModeledMatrixCard from './ModeledMatrixCard';
import PublishPlanButton from './PublishPlanButton';
import type { FindPublishedPlan, PublishPlanArgs } from './publishPlan';
import { fmtFte } from '../../lib/format';
import { useCurrency } from '../../lib/currency';

interface Props {
  model: Model;
  result: CalculationResult;
  profile: ClientProfile;
  /** The Actual plan attached to the scenario, when there is one — needed to
   *  build the Actual automation basis. */
  staffPlan?: StaffPlan | null;
  workspace?: ScenarioWorkspace;
  /** Patches the scenario profile. Needed for the Pre-Automation Release card's
   *  Adjust mode; without it that card renders read-only. */
  onProfileChange?: (patch: Partial<ClientProfile>) => void;
  /** Saves the Pre-Automation Release grid or the Final plan to the client
   *  workspace. Omitted in read-only contexts. */
  onPublishPlan?: (args: PublishPlanArgs) => Promise<void>;
  /** Resolves which saved plan a matrix would update in place. */
  findPublishedPlan?: FindPublishedPlan;
  /** Seed for the publish name field, e.g. the scenario's name. */
  defaultPlanName?: string;
}

type Year = 1 | 2 | 3;
/** What the whole page is looking at: a specific year, or every automation
 *  fully landed. One piece of state so the tiles, heatmap and post-automation
 *  matrix can never show different points in time. */
type Horizon = AutomationHorizon;

const YEARS: Year[] = [1, 2, 3];
const HORIZONS: Horizon[] = [1, 2, 3, 'full'];

// ---------------------------------------------------------------------------
// Per-cell impact math
// ---------------------------------------------------------------------------

interface CellImpact {
  disciplineId: string;
  disciplineLabel: string;
  channelId: string;
  channelLabel: string;
  levelId: LevelId;
  fte: number;
  blendedHourly: number;
  hubShift: number; // $ saved by the modeled shore split vs all-onshore baseline
  /** Per-year automation savings in $ (already ramped). */
  autoY: [number, number, number];
  /** Automation savings in $ once every automation has fully landed. */
  autoFull: number;
}

/**
 * Dollars per cell for the team the page is measuring.
 *
 * `cells` are the resolved basis cells, not the raw modeled grid: a hand
 * adjustment changes both legs of the impact — fewer FTE in a cell release
 * fewer hours and save less by sitting at a hub — so the tiles have to move with
 * the adjusted matrix or they would describe a team that is no longer on screen.
 * Every basis carries per-cell cost by the time it arrives here, including a
 * hand-entered Actual plan, which `staffPlanToBasisCells` prices off the rate
 * card.
 */
function buildCellImpacts(
  model: Model,
  cells: DisciplineChannelCell[],
  profile: ClientProfile,
): CellImpact[] {
  if (cells.length === 0) return [];

  const automations = scenarioAutomations(profile);
  const deployments = resolveAutomationDeployments(
    automations,
    profile.automationDeployments,
  );
  const enabled = automations.filter((a) => deployments.get(a.id)?.enabled);

  // Released hours per (automation, cell), grouped by cell so each cell can turn
  // its own hours into dollars at its own blended rate.
  const releasedByCell = new Map<string, { automationId: string; hoursAtFull: number }[]>();
  for (const impact of automationCellImpacts(model, cells, enabled)) {
    const arr = releasedByCell.get(impact.cellKey);
    if (arr) arr.push(impact);
    else releasedByCell.set(impact.cellKey, [impact]);
  }

  const out: CellImpact[] = [];

  for (const c of cells) {
    const fte = Number(c.adjustedFte) || 0;
    if (fte <= 0) continue;

    const onRate = Number(
      lookupCostPerRole(model, 'onshore', c.disciplineId, c.channelId, c.levelId),
    ) || 0;

    // Modeled cost is the engine's per-cell shore cost (driven by the
    // scenario's OGS config). The baseline is the all-onshore cost, so the
    // hub shift is the saving vs. staffing everything onshore.
    const baselineCost = fte * onRate;
    const modeledCost = Number(c.totalCost) || 0;
    const hubShift = baselineCost - modeledCost;

    const totalHours = fte * FTE_CAPACITY;
    const blendedHourly = totalHours > 0 ? modeledCost / totalHours : 0;

    // Per-year automation savings — each automation's full-impact dollars
    // ramped by its own deployment plan, summed into the cell.
    const autoY: [number, number, number] = [0, 0, 0];
    let autoFull = 0;
    const released = releasedByCell.get(
      staffPlanCellKey(c.disciplineId, c.channelId, c.levelId),
    );
    for (const impact of released || []) {
      const deployment = deployments.get(impact.automationId);
      if (!deployment) continue;
      const dollarsAtFull = impact.hoursAtFull * blendedHourly;
      autoFull += dollarsAtFull;
      autoY[0] += dollarsAtFull * automationAdoption(1, deployment);
      autoY[1] += dollarsAtFull * automationAdoption(2, deployment);
      autoY[2] += dollarsAtFull * automationAdoption(3, deployment);
    }

    out.push({
      disciplineId: c.disciplineId,
      disciplineLabel: c.disciplineLabel,
      channelId: c.channelId,
      channelLabel:
        c.channelId === 'all' ? 'All channels' : c.channelLabel || c.channelId,
      levelId: c.levelId,
      fte,
      blendedHourly,
      hubShift,
      autoY,
      autoFull,
    });
  }

  return out;
}

/** Automation dollars for a cell at a horizon. */
function autoAt(ci: CellImpact, horizon: Horizon): number {
  return horizon === 'full' ? ci.autoFull : ci.autoY[horizon - 1]!;
}

// ---------------------------------------------------------------------------
// Aggregate helpers
// ---------------------------------------------------------------------------

interface YearTotals {
  hubShift: number;
  automation: number;
  combined: number;
}

/** Hub shift is steady-state, so it counts in full at every horizon; only the
 *  automation leg ramps. */
function totalsAt(impacts: CellImpact[], horizon: Horizon): YearTotals {
  const t: YearTotals = { hubShift: 0, automation: 0, combined: 0 };
  for (const ci of impacts) {
    t.hubShift += ci.hubShift;
    t.automation += autoAt(ci, horizon);
  }
  t.combined = t.hubShift + t.automation;
  return t;
}

// ---------------------------------------------------------------------------
// Post-automation headcount
// ---------------------------------------------------------------------------

/** Headcount in a cell before automation, split by shore. */
interface BeforeSplit {
  on: number;
  hub: number;
  total: number;
}

/** Annual cost of a cell, split by shore, in the model's base currency. */
interface CostSplit {
  on: number;
  hub: number;
}

interface AfterAutomation {
  /** Post-automation cells, still in `DisciplineChannelCell` shape so the
   *  shared matrix grouping and `basisCellsToStaffPlanCells` both apply. */
  cells: DisciplineChannelCell[];
  /** The pre-automation cells, kept so rows taken to zero still render. */
  basisCells: DisciplineChannelCell[];
  /** Pre-automation split per cell, for showing what each number came down
   *  from. Keyed by `staffPlanCellKey`. */
  before: Map<string, BeforeSplit>;
  /** Cost of each cell before and after the release, keyed the same way. The
   *  matrix can show money instead of headcount, and the reduction scales cost
   *  with FTE, so both sides are worth keeping rather than re-deriving. */
  beforeCost: Map<string, CostSplit>;
  afterCost: Map<string, CostSplit>;
  releases: Map<string, AutomationCellRelease>;
  beforeFte: number;
  releasedFte: number;
  afterFte: number;
  beforeCostTotal: number;
  releasedCostTotal: number;
  afterCostTotal: number;
  impactedCells: number;
  /** Cells automation took all the way to zero — a role the plan no longer
   *  needs, which is a different statement from a role that merely shrank. */
  zeroedCells: number;
  /** Cells where stacked automations wanted more time than the cell held. */
  clampedCells: number;
  maxReleased: number;
  /** Largest cost released from any one cell, for shading the money view. A
   *  separate scale from `maxReleased`: the costliest cell to automate is not
   *  necessarily the one that gave up the most headcount. */
  maxReleasedCost: number;
}

/**
 * Subtract released time from each cell, floored at zero.
 *
 * The reduction splits across On and Hub in the cell's existing proportion: the
 * automation math has no shore dimension, so any other split would be invented.
 */
function buildAfterAutomation(
  basisCells: DisciplineChannelCell[],
  releases: Map<string, AutomationCellRelease>,
): AfterAutomation {
  const cells: DisciplineChannelCell[] = [];
  const before = new Map<string, BeforeSplit>();
  const beforeCost = new Map<string, CostSplit>();
  const afterCost = new Map<string, CostSplit>();
  let beforeFte = 0;
  let releasedFte = 0;
  let afterFte = 0;
  let beforeCostTotal = 0;
  let releasedCostTotal = 0;
  let afterCostTotal = 0;
  let impactedCells = 0;
  let zeroedCells = 0;
  let clampedCells = 0;
  let maxReleased = 0;
  let maxReleasedCost = 0;

  for (const c of basisCells) {
    const total = Number(c.adjustedFte) || 0;
    if (total <= 0) continue;
    const key = staffPlanCellKey(c.disciplineId, c.channelId, c.levelId);
    const on = Number(c.onshoreFte) || 0;
    const near = Number(c.nearshoreFte) || 0;
    const off = Number(c.offshoreFte) || 0;
    before.set(key, { on, hub: near + off, total });
    beforeFte += total;

    const release = releases.get(key);
    const released = Math.min(release?.fteReleased || 0, total);
    const after = total - released;
    const scale = total > 0 ? after / total : 0;

    // Cost follows the same scale as headcount: the shore split is unchanged by
    // the release, so a cell that loses a third of its FTE loses a third of its
    // cost at each shore.
    const onCost = Number(c.onshoreCost) || 0;
    const hubCost = (Number(c.nearshoreCost) || 0) + (Number(c.offshoreCost) || 0);
    beforeCost.set(key, { on: onCost, hub: hubCost });
    afterCost.set(key, { on: onCost * scale, hub: hubCost * scale });
    const cellBeforeCost = onCost + hubCost;
    const cellReleasedCost = cellBeforeCost * (1 - scale);
    beforeCostTotal += cellBeforeCost;
    releasedCostTotal += cellReleasedCost;
    afterCostTotal += cellBeforeCost * scale;

    releasedFte += released;
    afterFte += after;
    if (released > 0) {
      impactedCells += 1;
      if (released > maxReleased) maxReleased = released;
      if (cellReleasedCost > maxReleasedCost) maxReleasedCost = cellReleasedCost;
      if (after <= 0) zeroedCells += 1;
      if (release?.clamped) clampedCells += 1;
    }

    cells.push({
      ...c,
      baseFte: after,
      adjustedFte: after,
      onshoreFte: on * scale,
      nearshoreFte: near * scale,
      offshoreFte: off * scale,
      onshoreCost: (Number(c.onshoreCost) || 0) * scale,
      nearshoreCost: (Number(c.nearshoreCost) || 0) * scale,
      offshoreCost: (Number(c.offshoreCost) || 0) * scale,
      totalCost: (Number(c.totalCost) || 0) * scale,
    });
  }

  return {
    cells,
    basisCells,
    before,
    beforeCost,
    afterCost,
    releases,
    beforeFte,
    releasedFte,
    afterFte,
    beforeCostTotal,
    releasedCostTotal,
    afterCostTotal,
    impactedCells,
    zeroedCells,
    clampedCells,
    maxReleased,
    maxReleasedCost,
  };
}

interface HeatmapRow {
  key: string;
  disciplineId: string;
  disciplineLabel: string;
  channelId: string;
  channelLabel: string;
  byGroup: Map<LevelGroupId, number>;
  total: number;
}

function buildHeatmap(
  impacts: CellImpact[],
  horizon: Horizon,
): { rows: HeatmapRow[]; max: number; total: number } {
  const map = new Map<string, HeatmapRow>();
  for (const ci of impacts) {
    const key = `${ci.disciplineId}::${ci.channelId}`;
    let row = map.get(key);
    if (!row) {
      row = {
        key,
        disciplineId: ci.disciplineId,
        disciplineLabel: ci.disciplineLabel,
        channelId: ci.channelId,
        channelLabel: ci.channelLabel,
        byGroup: new Map(),
        total: 0,
      };
      map.set(key, row);
    }
    const value = ci.hubShift + autoAt(ci, horizon);
    const groupId = levelGroupOf(ci.levelId).id;
    row.byGroup.set(groupId, (row.byGroup.get(groupId) || 0) + value);
    row.total += value;
  }

  let max = 0;
  let total = 0;
  for (const r of map.values()) {
    total += r.total;
    for (const v of r.byGroup.values()) {
      if (v > max) max = v;
    }
  }

  const rows = Array.from(map.values()).sort((a, b) => {
    if (a.disciplineLabel !== b.disciplineLabel) {
      return a.disciplineLabel.localeCompare(b.disciplineLabel);
    }
    if (a.channelId === 'all') return -1;
    if (b.channelId === 'all') return 1;
    return a.channelLabel.localeCompare(b.channelLabel);
  });

  return { rows, max, total };
}

// Slate→emerald gradient — savings = good. Shared heatmap helpers keep the
// gradient math identical to the other result-view cards.
const PALETTE = HEATMAP_EMERALD;
const bgFor = (value: number, max: number) => bgForPalette(value, max, PALETTE);
const txtFor = (value: number, max: number) => txtForPalette(value, max, PALETTE);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SummaryView({
  model,
  result,
  profile,
  staffPlan,
  workspace = 'advanced',
  onProfileChange,
  onPublishPlan,
  findPublishedPlan,
  defaultPlanName,
}: Props) {
  const [horizon, setHorizon] = useState<Horizon>(3);
  const { money } = useCurrency();

  // Every number on this page is measured on the basis chosen on the Automation
  // Impact tab — the modeled team, that team with manual edits, or the client's
  // Actual plan. One resolution feeds the dollar tiles, the heatmap and the
  // post-automation matrix, so an edit cannot move one and leave the others
  // describing the team it replaced.
  // Basic scenarios lock to the OGS-adjusted staff plan.
  const basis: AutomationBasis = useMemo(
    () => resolveBasis(profile, availableBases(profile, staffPlan, workspace), workspace),
    [profile, staffPlan, workspace],
  );
  const releaseBasisCells = useMemo(
    () => cellsForBasis(basis, model, result, profile, staffPlan, workspace),
    [basis, model, result, profile, staffPlan, workspace],
  );

  const impacts = useMemo(
    () => buildCellImpacts(model, releaseBasisCells, profile),
    [model, releaseBasisCells, profile],
  );

  const yearTotals = useMemo(
    () => YEARS.map((y) => totalsAt(impacts, y)) as [YearTotals, YearTotals, YearTotals],
    [impacts],
  );
  const activeTotals = useMemo(() => totalsAt(impacts, horizon), [impacts, horizon]);
  const heatmap = useMemo(() => buildHeatmap(impacts, horizon), [impacts, horizon]);

  const releases = useMemo(
    () =>
      automationReleaseByCell(
        model,
        releaseBasisCells,
        profile.automations,
        profile.automationDeployments,
        horizon,
      ),
    [model, releaseBasisCells, profile.automations, profile.automationDeployments, horizon],
  );
  const after = useMemo(
    () => buildAfterAutomation(releaseBasisCells, releases),
    [releaseBasisCells, releases],
  );

  const activeGroups = useMemo<LevelGroup[]>(() => {
    const present = new Set<LevelGroupId>();
    for (const r of heatmap.rows) {
      for (const g of r.byGroup.keys()) present.add(g);
    }
    return LEVEL_GROUPS.filter((g) => present.has(g.id));
  }, [heatmap.rows]);

  const totalImpact = activeTotals.combined;
  const cumulative = yearTotals[0]!.combined + yearTotals[1]!.combined + yearTotals[2]!.combined;
  const horizonTag = automationHorizonLabel(horizon);

  if (impacts.length === 0) {
    return (
      <section className="card">
        <div className="card-body text-sm text-slate-500">
          No FTE × cost output yet. Once the scenario produces engine results
          and the model has Cost-per-Role rates, this view will combine
          hub-shift + automation savings into a Y1/Y2/Y3 picture.
        </div>
      </section>
    );
  }

  return (
    <div className="space-y-4">
      {/* 3-year tile strip + cumulative */}
      <section className="card">
        <div className="card-header">
          <div>
            <h3 className="text-base font-semibold flex items-center gap-2">
              <BarChart3 size={16} className="text-brand-purple-500" />
              Summary — combined dollar impact, Y1–Y3
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              <span className="inline-flex items-center gap-1 mr-3">
                <span className="inline-block w-2 h-2 rounded-sm bg-sky-400" />
                Hub shift
              </span>
              <span className="inline-flex items-center gap-1 mr-3">
                <span className="inline-block w-2 h-2 rounded-sm bg-emerald-400" />
                Automation
              </span>
              Hub-shift savings are steady-state; automation savings ramp per
              the deployment plan saved on each automation row. Measured on the{' '}
              <span className="font-medium text-slate-700">
                {basisLabel(basis)}
              </span>{' '}
              team.
            </p>
          </div>
          <div className="rounded-lg border border-brand-purple-200 bg-brand-purple-50/40 px-3 py-1.5 text-right">
            <div className="text-[10px] uppercase tracking-wider text-brand-purple-700/70 font-semibold">
              Cumulative Y1–Y3
            </div>
            <div className="text-lg font-bold text-brand-purple-800 tabular-nums">
              {money(cumulative)}
            </div>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {YEARS.map((y) => (
              <YearTile
                key={y}
                year={y}
                totals={yearTotals[y - 1]!}
                active={horizon === y}
                onSelect={() => setHorizon(y)}
              />
            ))}
          </div>
          {horizon === 'full' && (
            <p className="mt-3 text-xs text-slate-500">
              Viewing <span className="font-medium text-slate-700">full impact</span> —
              every automation fully landed, which no single year reaches. Pick a
              year tile to go back to a ramped view.
            </p>
          )}
        </div>
      </section>

      {/* Heatmap */}
      <section className="card">
        <div className="card-header">
          <div className="min-w-0">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <Coins size={16} className="text-brand-purple-500" />
              Where the dollars come from — Discipline × Channel × Level
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Cell shading scales with combined {automationHorizonWords(horizon)}{' '}
              impact on the {basisLabel(basis)} team. The horizon applies to this
              whole page.
            </p>
          </div>
          <HorizonTabs horizon={horizon} onChange={setHorizon} />
        </div>
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs tabular-nums">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left py-2 pl-4 pr-3 font-medium sticky left-0 bg-white z-10">
                    Discipline × Channel
                  </th>
                  {activeGroups.map((g) => (
                    <th key={g.id} className="text-center py-2 px-2 font-medium">
                      <div className="font-semibold text-slate-700">{g.name}</div>
                      <div className="text-[9px] text-slate-400 font-normal normal-case">
                        {g.levelIds.join('–')}
                      </div>
                    </th>
                  ))}
                  <th className="text-right py-2 pr-4 pl-2 font-medium border-l border-slate-200 bg-slate-50/40">
                    Row total
                  </th>
                </tr>
              </thead>
              <tbody>
                {heatmap.rows.map((row, i) => (
                  <tr
                    key={row.key}
                    className={clsx(
                      'border-b border-slate-100 last:border-b-0',
                      i % 2 === 1 && 'bg-slate-50/30',
                    )}
                  >
                    <td className="py-1.5 pl-4 pr-3 sticky left-0 bg-inherit z-10">
                      <div className="font-medium text-slate-800 leading-tight">
                        {row.disciplineLabel}
                      </div>
                      <div className="text-[10px] text-slate-500 leading-tight">
                        {row.channelLabel}
                      </div>
                    </td>
                    {activeGroups.map((g) => {
                      const v = row.byGroup.get(g.id) || 0;
                      const dim = v < 0.5;
                      return (
                        <td
                          key={g.id}
                          className="py-1 px-1 text-center"
                          style={{
                            backgroundColor: dim ? 'transparent' : bgFor(v, heatmap.max),
                            color: dim ? '#cbd5e1' : txtFor(v, heatmap.max),
                          }}
                          title={`${row.disciplineLabel} · ${row.channelLabel} · ${g.label} — ${money(
                            v,
                          )}`}
                        >
                          <span className="font-medium">{dim ? '·' : money(v)}</span>
                        </td>
                      );
                    })}
                    <td className="py-1 pr-4 pl-2 text-right font-semibold text-slate-700 border-l border-slate-200 bg-slate-50/40">
                      {money(row.total)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-brand-purple-50/40 font-semibold">
                  <td className="py-2 pl-4 pr-3 text-brand-purple-800 sticky left-0 bg-brand-purple-50/40 z-10">
                    Total {horizonTag}
                  </td>
                  {activeGroups.map((g) => {
                    const colTotal = heatmap.rows.reduce(
                      (sum, r) => sum + (r.byGroup.get(g.id) || 0),
                      0,
                    );
                    return (
                      <td key={g.id} className="py-2 px-2 text-center text-brand-purple-800">
                        {money(colTotal)}
                      </td>
                    );
                  })}
                  <td className="py-2 pr-4 pl-2 text-right text-brand-purple-800 border-l border-slate-200 bg-brand-purple-50/40">
                    {money(totalImpact)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-slate-100 flex flex-wrap items-center gap-x-5 gap-y-1 text-[10px] uppercase tracking-wider text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block w-3 h-3 rounded-sm border border-slate-200"
                style={{ background: 'rgb(248,250,252)' }}
              />
              Low impact
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block w-3 h-3 rounded-sm"
                style={{ background: 'rgb(127,200,179)' }}
              />
              Mid
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block w-3 h-3 rounded-sm"
                style={{ background: 'rgb(5,150,105)' }}
              />
              High
            </span>
            <span className="ml-auto text-slate-500">
              Sum of hub-shift $ + automation $ at {automationHorizonWords(horizon)}{' '}
              per cell.
            </span>
          </div>
        </div>
      </section>

      {/* The staffing automation is released from, directly above what it turns
          into. Mounted from the same component as the Staff Plan tab, so an
          edit made here shows up there and vice versa. Basic scenarios have no
          modeled grid — release already starts from the OGS-adjusted plan. */}
      {workspace !== 'basic' && (
        <ModeledMatrixCard
          model={model}
          result={result}
          profile={profile}
          onProfileChange={onProfileChange}
          onPublishPlan={onPublishPlan}
          findPublishedPlan={findPublishedPlan}
          defaultPlanName={defaultPlanName}
          eyebrow="Pre-Automation Release"
          notice={
            basis === 'actual' ? (
              <>
                Automation is being released against the{' '}
                <span className="font-medium text-slate-600">Actual</span> plan
                picked on the Automation Impact tab, so the matrix below starts
                from that roster rather than this grid.
              </>
            ) : undefined
          }
        />
      )}

      <AfterAutomationCard
        model={model}
        after={after}
        horizon={horizon}
        basis={basis}
      />

      <FinalPlanCard
        model={model}
        profile={profile}
        after={after}
        horizon={horizon}
        onHorizonChange={setHorizon}
        basis={basis}
        onPublishPlan={onPublishPlan}
        findPublishedPlan={findPublishedPlan}
        defaultPlanName={defaultPlanName}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function YearTile({
  year,
  totals,
  active,
  onSelect,
}: {
  year: Year;
  totals: YearTotals;
  active: boolean;
  onSelect: () => void;
}) {
  const { money } = useCurrency();
  const total = totals.combined;
  const hubPct = total > 0 ? totals.hubShift / total : 0;
  const autoPct = total > 0 ? totals.automation / total : 0;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={clsx(
        'rounded-lg border px-4 py-3 text-left transition-all',
        active
          ? 'border-brand-purple-300 bg-brand-purple-50/60 ring-2 ring-brand-purple-200 shadow-sm'
          : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm',
      )}
    >
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          Year {year}
        </div>
        {active && (
          <span className="text-[10px] uppercase tracking-wider text-brand-purple-600 font-semibold">
            Viewing
          </span>
        )}
      </div>
      <div className="mt-1 text-2xl font-bold text-slate-900 tabular-nums leading-tight">
        {money(total)}
      </div>
      <div className="text-[11px] text-slate-500">total annual savings</div>
      {/* Stacked composition bar */}
      <div className="mt-3 h-1.5 rounded-full overflow-hidden flex bg-slate-100">
        <div
          className="bg-sky-400"
          style={{ width: `${hubPct * 100}%` }}
          title={`Hub shift ${money(totals.hubShift)} · ${(hubPct * 100).toFixed(0)}%`}
        />
        <div
          className="bg-emerald-400"
          style={{ width: `${autoPct * 100}%` }}
          title={`Automation ${money(totals.automation)} · ${(autoPct * 100).toFixed(0)}%`}
        />
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
        <Leg
          label="Hub shift"
          value={totals.hubShift}
          icon={<span className="inline-block w-2 h-2 rounded-sm bg-sky-400" />}
        />
        <Leg
          label="Automation"
          value={totals.automation}
          icon={<Sparkles size={10} className="text-emerald-600" />}
        />
      </div>
    </button>
  );
}

function Leg({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
}) {
  const { money } = useCurrency();
  const positive = value > 0;
  const negligible = Math.abs(value) < 0.5;
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1 text-slate-500">
        {icon}
        <span className="uppercase tracking-wider text-[9px] font-semibold">
          {label}
        </span>
      </div>
      <div
        className={clsx(
          'tabular-nums font-medium',
          negligible
            ? 'text-slate-400'
            : positive
              ? 'text-emerald-700'
              : 'text-rose-600',
        )}
      >
        {positive && !negligible && '+'}
        {money(value)}
      </div>
    </div>
  );
}

function HorizonTabs({
  horizon,
  onChange,
}: {
  horizon: Horizon;
  onChange: (h: Horizon) => void;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-0.5 inline-flex shadow-sm">
      {HORIZONS.map((h) => {
        const active = h === horizon;
        return (
          <button
            key={String(h)}
            type="button"
            onClick={() => onChange(h)}
            title={
              h === 'full'
                ? 'Every automation fully landed, ignoring ramp'
                : `Average adoption across ${automationHorizonWords(h)}`
            }
            className={clsx(
              'px-3 py-1 rounded-md text-xs font-medium transition-all',
              active
                ? 'bg-brand-purple-50 text-brand-purple-700 ring-1 ring-brand-purple-200'
                : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50',
            )}
          >
            {automationHorizonLabel(h)}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Staffing after automation — the matrix, read only
// ---------------------------------------------------------------------------

/**
 * What the cells report: headcount or annual cost, and either where the team
 * lands after the release or how much of it the release took off.
 *
 * Both halves matter on their own. "How many roles did this free up" and "what
 * is that worth" are different questions, and each is asked either about the
 * remaining team or about the reduction.
 */
type MatrixMode = 'fte_total' | 'fte_change' | 'money_total' | 'money_change';

const MATRIX_MODES: [MatrixMode, string, string][] = [
  ['fte_total', 'FTE Total', 'Where headcount lands after automation'],
  ['fte_change', 'FTE Change', 'How much headcount automation took off each cell'],
  ['money_total', '$ Total', 'Annual cost of the team that remains after automation'],
  ['money_change', '$ Change', 'Annual cost automation took off each cell'],
];

/**
 * Shows where headcount lands once automation has been released, and nothing
 * more. Saving belongs to the Final Staff Plan card below it: this matrix moves
 * with the horizon tabs, and a matrix that changes under the cursor is the wrong
 * place to commit anything from.
 */
function AfterAutomationCard({
  model,
  after,
  horizon,
  basis,
}: {
  model: Model;
  after: AfterAutomation;
  horizon: Horizon;
  basis: AutomationBasis;
}) {
  const [mode, setMode] = useState<MatrixMode>('fte_total');
  const { money, display } = useCurrency();

  const disciplineOrder = useMemo(
    () => (model.disciplines || []).map((d) => ({ id: d.id, label: d.label })),
    [model.disciplines],
  );
  const channelOrder = useMemo(
    () => (model.channels || []).map((c) => ({ id: c.id, label: c.label })),
    [model.channels],
  );

  // Group the post-automation cells, using the pre-automation cells as the row
  // skeleton so a role automation took to zero still has a row saying so.
  const groups = useMemo(
    () => buildGroups(after.cells, disciplineOrder, channelOrder, after.basisCells),
    [after.cells, after.basisCells, disciplineOrder, channelOrder],
  );
  // Columns come from the pre-automation matrix, so a level group that
  // automates away entirely does not silently drop out of the comparison.
  const columns = useMemo(
    () =>
      activeColumnsFor(
        buildGroups(after.basisCells, disciplineOrder, channelOrder),
        GROUP_COLUMNS,
      ),
    [after.basisCells, disciplineOrder, channelOrder],
  );

  const rows = useMemo(
    () => groups.flatMap((g) => g.rows.map((r) => ({ group: g, row: r }))),
    [groups],
  );

  const keysFor = (disciplineId: string, channelId: string, col: MatrixColumn) =>
    col.levelIds.map((l) => staffPlanCellKey(disciplineId, channelId, l));

  // Post-automation cost per column, for the money view's total row. Cost is
  // held per cell rather than on the grouped matrix, so the column totals are
  // summed from the same keys the cells read.
  const costByColumn = useMemo(() => {
    const totals = new Map<string, CostSplit>();
    for (const { group, row } of rows) {
      for (const col of columns) {
        for (const key of keysFor(group.disciplineId, row.channelId, col)) {
          const cost = after.afterCost.get(key);
          if (!cost) continue;
          const cur = totals.get(col.key);
          if (cur) {
            cur.on += cost.on;
            cur.hub += cost.hub;
          } else {
            totals.set(col.key, { on: cost.on, hub: cost.hub });
          }
        }
      }
    }
    return totals;
  }, [rows, columns, after.afterCost]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shading scale for the grouped cells, so the heaviest group cell is darkest.
  const shadeMax = useMemo(() => {
    let fte = 0;
    let cost = 0;
    for (const { group, row } of rows) {
      for (const col of columns) {
        const agg = aggregateAfterCells(after, keysFor(group.disciplineId, row.channelId, col));
        if (agg.released > fte) fte = agg.released;
        if (agg.releasedCost > cost) cost = agg.releasedCost;
      }
    }
    return { fte, cost };
  }, [rows, columns, after]); // eslint-disable-line react-hooks/exhaustive-deps

  if (after.basisCells.length === 0 || columns.length === 0) return null;

  const basisWords = basisLabel(basis);
  const horizonWords = automationHorizonWords(horizon);
  const cash = mode === 'money_total' || mode === 'money_change';

  return (
    <section className="card">
      <div className="card-header">
        <div className="min-w-0">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Sparkles size={16} className="text-emerald-600" />
            Staffing after automation
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            The {basisWords} team with time released by automation at{' '}
            {horizonWords} taken out. Reductions split across On and Hub in each
            cell's existing proportion.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded-lg border border-slate-200 bg-white p-0.5 inline-flex shadow-sm">
            {MATRIX_MODES.map(([id, label, hint]) => (
              <button
                key={id}
                type="button"
                onClick={() => setMode(id)}
                title={hint}
                className={clsx(
                  'px-2.5 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap',
                  mode === id
                    ? 'bg-brand-purple-50 text-brand-purple-700 ring-1 ring-brand-purple-200'
                    : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50',
                )}
              >
                {label.replace('$', currencySymbol(display))}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="card-body p-0">
        <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-x-6 gap-y-2">
          {/* The strip follows the matrix: reading dollars in the grid and FTE in
              the summary above it invites comparing the two by mistake. */}
          <MatrixStat
            label="Before"
            value={cash ? after.beforeCostTotal : after.beforeFte}
            cash={cash}
          />
          <MatrixStat
            label="Released"
            value={cash ? after.releasedCostTotal : after.releasedFte}
            cash={cash}
            tone="emerald"
          />
          <MatrixStat
            label="After"
            value={cash ? after.afterCostTotal : after.afterFte}
            cash={cash}
            strong
          />
          <div className="text-[11px] text-slate-500">
            {after.impactedCells === 0
              ? 'No automation releases time at this horizon.'
              : `Automation touches ${after.impactedCells} cell${
                  after.impactedCells === 1 ? '' : 's'
                }${
                  after.zeroedCells > 0
                    ? `, ${after.zeroedCells} of them to zero`
                    : ''
                }.`}
          </div>
        </div>

        {after.clampedCells > 0 && (
          <div className="px-4 py-2 border-b border-slate-100 bg-amber-50/60 text-[11px] text-amber-900">
            Stacked automations wanted more time than {after.clampedCells} cell
            {after.clampedCells === 1 ? '' : 's'} hold, so the release was capped
            at that cell's headcount. Worth checking those automations do not
            overlap on the same work.
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-xs tabular-nums">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th
                  rowSpan={2}
                  className="text-left align-bottom py-2 pl-4 pr-3 font-medium sticky left-0 bg-white z-10"
                >
                  Discipline × Channel
                </th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    colSpan={2}
                    className="text-center pt-2 pb-1 px-1 font-semibold text-slate-600 border-b border-slate-100 whitespace-nowrap normal-case tracking-normal"
                  >
                    {col.label}
                  </th>
                ))}
                <th
                  rowSpan={2}
                  className="text-right align-bottom py-2 pl-3 pr-2 font-medium"
                >
                  After
                </th>
                <th
                  rowSpan={2}
                  className="text-right align-bottom py-2 pr-4 pl-2 font-medium border-l border-slate-200 bg-slate-50/40"
                >
                  Released
                </th>
              </tr>
              <tr className="text-[9px] tracking-wider text-slate-400">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    colSpan={2}
                    className="p-0 border-b border-slate-200"
                  >
                    <div className="flex">
                      <span className="flex-1 text-right pb-1.5 pr-1 font-medium border-l border-slate-100">
                        On
                      </span>
                      <span className="flex-1 text-right pb-1.5 pl-1 pr-2 font-medium bg-amber-50/40 text-amber-700/70">
                        Hub
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ group, row }, i) => {
                let rowReleased = 0;
                let rowAfterCost = 0;
                let rowReleasedCost = 0;
                for (const col of columns) {
                  for (const key of keysFor(group.disciplineId, row.channelId, col)) {
                    rowReleased += Math.min(
                      after.releases.get(key)?.fteReleased || 0,
                      after.before.get(key)?.total || 0,
                    );
                    const wasCost = after.beforeCost.get(key);
                    const nowCost = after.afterCost.get(key);
                    const was = (wasCost?.on || 0) + (wasCost?.hub || 0);
                    const now = (nowCost?.on || 0) + (nowCost?.hub || 0);
                    rowAfterCost += now;
                    rowReleasedCost += Math.max(was - now, 0);
                  }
                }
                return (
                  <tr
                    key={`${group.disciplineId}::${row.channelId}`}
                    className={clsx(
                      'border-b border-slate-100 last:border-b-0',
                      i % 2 === 1 && 'bg-slate-50/30',
                    )}
                  >
                    <td className="py-1.5 pl-4 pr-3 sticky left-0 bg-inherit z-10">
                      <div className="font-medium text-slate-800 leading-tight">
                        {group.disciplineLabel}
                      </div>
                      <div className="text-[10px] text-slate-500 leading-tight">
                        {row.channelLabel}
                      </div>
                    </td>
                    {columns.map((col) => (
                      <AfterCell
                        key={col.key}
                        cellKeys={keysFor(group.disciplineId, row.channelId, col)}
                        rowLabel={`${group.disciplineLabel} · ${row.channelLabel} · ${col.label}`}
                        after={columnSplit(row.byLevel, col)}
                        state={after}
                        mode={mode}
                        maxReleased={shadeMax.fte}
                        maxReleasedCost={shadeMax.cost}
                      />
                    ))}
                    <td className="py-1.5 pl-3 pr-2 text-right font-semibold text-slate-700">
                      {cash ? money(rowAfterCost) : fmtFte(row.total)}
                    </td>
                    <td className="py-1.5 pr-4 pl-2 text-right border-l border-slate-200 bg-slate-50/40">
                      <span
                        className={clsx(
                          (cash ? rowReleasedCost > 0.5 : rowReleased > 0.05)
                            ? 'text-emerald-700 font-medium'
                            : 'text-slate-300',
                        )}
                      >
                        {cash
                          ? rowReleasedCost > 0.5
                            ? `−${money(rowReleasedCost)}`
                            : '·'
                          : rowReleased > 0.05
                            ? `−${fmtFte(rowReleased)}`
                            : '·'}
                      </span>
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-brand-purple-50/40 font-semibold">
                <td className="py-2 pl-4 pr-3 text-brand-purple-800 sticky left-0 bg-brand-purple-50/40 z-10">
                  Total
                </td>
                {columns.map((col) => {
                  const on = groups.reduce(
                    (sum, g) => sum + columnSplit(g.byLevel, col).on,
                    0,
                  );
                  const hub = groups.reduce(
                    (sum, g) => sum + columnSplit(g.byLevel, col).hub,
                    0,
                  );
                  const levelCost = costByColumn.get(col.key);
                  return (
                    <td key={col.key} colSpan={2} className="p-0">
                      <div className="flex text-brand-purple-800">
                        <span className="flex-1 py-2 pr-1 text-right border-l border-slate-100">
                          {cash ? money(levelCost?.on || 0) : fmtFte(on)}
                        </span>
                        <span className="flex-1 py-2 pl-1 pr-2 text-right bg-amber-50/40 text-amber-800">
                          {cash ? money(levelCost?.hub || 0) : fmtFte(hub)}
                        </span>
                      </div>
                    </td>
                  );
                })}
                <td className="py-2 pl-3 pr-2 text-right text-brand-purple-800">
                  {cash ? money(after.afterCostTotal) : fmtFte(after.afterFte)}
                </td>
                <td className="py-2 pr-4 pl-2 text-right text-emerald-700 border-l border-slate-200 bg-brand-purple-50/40">
                  −{cash ? money(after.releasedCostTotal) : fmtFte(after.releasedFte)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-slate-100 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[10px] uppercase tracking-wider text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-3 h-3 rounded-sm"
              style={{ background: 'rgb(167,216,201)' }}
            />
            Automation reduced
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-white border border-dashed border-rose-300" />
            Reduced to zero
          </span>
          <span className="ml-auto normal-case tracking-normal text-slate-500">
            {cash
              ? `Annual cost of the roles that remain, priced at each cell's own shore
                 rates. Cost falls with headcount, so it carries the same caveat.`
              : `Post-automation headcount ties to neither the scenario budget nor
                 spend per FTE — it is what the team looks like once the released
                 time is taken out.`}
          </span>
        </div>
      </div>
    </section>
  );
}

/** One headline figure for a matrix, in FTE or in annual cost. */
function MatrixStat({
  label,
  value,
  tone,
  strong,
  cash,
}: {
  label: string;
  value: number;
  tone?: 'emerald';
  strong?: boolean;
  cash?: boolean;
}) {
  const { money, display } = useCurrency();
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </div>
      <div
        className={clsx(
          'tabular-nums leading-tight',
          strong ? 'text-xl font-bold' : 'text-lg font-semibold',
          tone === 'emerald' ? 'text-emerald-700' : 'text-slate-900',
        )}
      >
        {tone === 'emerald' && value > 0 ? '−' : ''}
        {cash ? money(value) : fmtFte(value)}
      </div>
      <div className="text-[10px] text-slate-500">
        {cash ? `${display} / year` : 'FTE'}
      </div>
    </div>
  );
}

/** One level's On/Hub pair for a row. Tint scales with how much automation took
 *  off the cell; a cell taken all the way to zero is marked differently, since
 *  "this role shrank" and "this role is gone" are different conclusions. */
/** Before / released / cost totals over a set of exact-level cells. */
function aggregateAfterCells(state: AfterAutomation, cellKeys: readonly string[]) {
  const before = { on: 0, hub: 0, total: 0 };
  const costWas = { on: 0, hub: 0 };
  const costNow = { on: 0, hub: 0 };
  let released = 0;
  let clamped = false;
  for (const k of cellKeys) {
    const b = state.before.get(k);
    const bTotal = b?.total || 0;
    before.on += b?.on || 0;
    before.hub += b?.hub || 0;
    before.total += bTotal;
    const r = state.releases.get(k);
    released += Math.min(r?.fteReleased || 0, bTotal);
    if (r?.clamped) clamped = true;
    const was = state.beforeCost.get(k);
    costWas.on += was?.on || 0;
    costWas.hub += was?.hub || 0;
    const now = state.afterCost.get(k);
    costNow.on += now?.on || 0;
    costNow.hub += now?.hub || 0;
  }
  const releasedCost = Math.max(
    costWas.on + costWas.hub - (costNow.on + costNow.hub),
    0,
  );
  return { before, released, clamped, costWas, costNow, releasedCost };
}

function AfterCell({
  cellKeys,
  rowLabel,
  after,
  state,
  mode,
  maxReleased,
  maxReleasedCost,
}: {
  cellKeys: readonly string[];
  rowLabel: string;
  after: LevelSplit | undefined;
  state: AfterAutomation;
  mode: MatrixMode;
  maxReleased: number;
  maxReleasedCost: number;
}) {
  const { money, moneyExact } = useCurrency();
  const cash = mode === 'money_total' || mode === 'money_change';
  const change = mode === 'fte_change' || mode === 'money_change';
  const agg = aggregateAfterCells(state, cellKeys);
  const before = agg.before;
  const beforeTotal = before.total;
  const released = agg.released;
  const afterOn = after?.on || 0;
  const afterHub = after?.hub || 0;
  const afterTotal = afterOn + afterHub;
  const zeroed = released > 0 && afterTotal <= 0.005 && beforeTotal > 0;
  const touched = released > 0.005;

  const costWas = agg.costWas;
  const costOn = agg.costNow.on;
  const costHub = agg.costNow.hub;
  const costBeforeTotal = costWas.on + costWas.hub;
  const releasedCost = agg.releasedCost;

  const capped = agg.clamped ? ' (release capped)' : '';
  const title = !beforeTotal
    ? rowLabel
    : cash
      ? `${rowLabel} — ${moneyExact(costBeforeTotal)} before, ${moneyExact(
          releasedCost,
        )} released, ${moneyExact(costOn + costHub)} after, per year${capped}`
      : `${rowLabel} — ${fmtFte(beforeTotal, 2)} before, ${fmtFte(
          released,
          2,
        )} released, ${fmtFte(afterTotal, 2)} after${capped}`;

  // Shaded by what the release took off this cell, in whichever unit is on
  // screen: the costliest cell to automate is not always the one that gave up
  // the most headcount.
  const background = touched && !zeroed
    ? bgFor(cash ? releasedCost : released, cash ? maxReleasedCost : maxReleased)
    : undefined;
  const tint = (v: number) => txtFor(v, cash ? maxReleasedCost : maxReleased);

  // One leg of the cell — On or Hub — in whichever of the four readings is on
  // screen. A change reading shows the reduction as a negative and says nothing
  // where the release did not reach.
  const show = (leg: {
    fte: number;
    fteReleased: number;
    cost: number;
    costReleased: number;
  }) => {
    if (cash) {
      if (change) {
        return leg.costReleased > 0.5 ? `−${money(leg.costReleased)}` : '·';
      }
      return leg.cost > 0.5 ? money(leg.cost) : '·';
    }
    if (change) {
      return leg.fteReleased > 0.005 ? `−${fmtFte(leg.fteReleased)}` : '·';
    }
    return leg.fte > 0.005 ? fmtFte(leg.fte) : '·';
  };

  const onLeg = {
    fte: afterOn,
    fteReleased: before.on - afterOn,
    cost: costOn,
    costReleased: Math.max(costWas.on - costOn, 0),
  };
  const hubLeg = {
    fte: afterHub,
    fteReleased: before.hub - afterHub,
    cost: costHub,
    costReleased: Math.max(costWas.hub - costHub, 0),
  };

  /** Whether this leg has a number to show in the current reading, so an empty
   *  one greys out instead of drawing the eye. */
  const filled = (leg: typeof onLeg) =>
    change
      ? cash
        ? leg.costReleased > 0.5
        : leg.fteReleased > 0.005
      : cash
        ? leg.cost > 0.5
        : leg.fte > 0.005;

  return (
    <td colSpan={2} className="p-0" title={title}>
      <div
        className={clsx(
          'flex',
          zeroed && 'border border-dashed border-rose-300 rounded-sm',
        )}
        style={background ? { backgroundColor: background } : undefined}
      >
        <span
          className={clsx(
            'flex-1 py-1.5 pr-1 text-right border-l border-slate-100',
            zeroed
              ? 'text-rose-500 font-medium'
              : touched
                ? 'font-medium'
                : filled(onLeg)
                  ? 'text-slate-700'
                  : 'text-slate-300',
          )}
          style={
            touched && !zeroed
              ? { color: tint(cash ? releasedCost : released) }
              : undefined
          }
        >
          {/* A cell taken to zero reads "0" of the team that remains, but in a
              change reading it is the whole reduction and has to say so. */}
          {zeroed && !change ? '0' : show(onLeg)}
        </span>
        <span
          className={clsx(
            'flex-1 py-1.5 pl-1 pr-2 text-right',
            !touched && 'bg-amber-50/40',
            zeroed
              ? 'text-rose-500 font-medium'
              : touched
                ? 'font-medium'
                : filled(hubLeg)
                  ? 'text-amber-800'
                  : 'text-slate-300',
          )}
          style={
            touched && !zeroed
              ? { color: tint(cash ? releasedCost : released) }
              : undefined
          }
        >
          {zeroed && !change ? '0' : show(hubLeg)}
        </span>
      </div>
    </td>
  );
}

// ---------------------------------------------------------------------------
// Final Staff Plan — the one saveable output of the release
// ---------------------------------------------------------------------------

/**
 * Per-automation contributions to record on a saved plan.
 *
 * These are uncapped: each automation's own effect at its own ramp. Where
 * stacked automations wanted more time than a cell held, the per-cell release
 * was capped, so this can total slightly more than the plan's `releasedFte` —
 * which is the honest way round, since the cap is a property of the cell rather
 * than of any one automation.
 */
function snapshotAutomations(
  model: Model,
  profile: ClientProfile,
  after: AfterAutomation,
  horizon: Horizon,
): StaffPlanSnapshotAutomation[] {
  const automations = scenarioAutomations(profile);
  const deployments = resolveAutomationDeployments(automations, profile.automationDeployments);
  const out: StaffPlanSnapshotAutomation[] = [];
  for (const a of automations) {
    const deployment = deployments.get(a.id);
    if (!deployment?.enabled) continue;
    const adoption = automationAdoption(horizon, deployment);
    const releasedFte =
      automationTotals(model, after.basisCells, a).fteAtFull * adoption;
    if (releasedFte <= 0) continue;
    out.push({
      id: a.id,
      name: a.label,
      startMonth: deployment.startMonth,
      rampMonths: deployment.rampMonths,
      adoption,
      releasedFte,
    });
  }
  return out.sort((x, y) => y.releasedFte - x.releasedFte);
}

/**
 * Freezes the post-automation matrix as a saved staff plan.
 *
 * Kept apart from the matrix above because saving is a decision, not a view: the
 * matrix moves with the horizon tabs, while this states plainly what would be
 * written down and records the scenario inputs that produced it. Those inputs are
 * a snapshot rather than a link — a scenario keeps being edited, and a plan
 * described by inputs that no longer produced it explains nothing.
 *
 * A Final plan is the end of the chain: it cannot be attached to a scenario as
 * its Actual team, so automation can never be released from it a second time.
 */
function FinalPlanCard({
  model,
  profile,
  after,
  horizon,
  onHorizonChange,
  basis,
  onPublishPlan,
  findPublishedPlan,
  defaultPlanName,
}: {
  model: Model;
  profile: ClientProfile;
  after: AfterAutomation;
  horizon: Horizon;
  onHorizonChange: (h: Horizon) => void;
  basis: AutomationBasis;
  onPublishPlan?: (args: PublishPlanArgs) => Promise<void>;
  findPublishedPlan?: FindPublishedPlan;
  defaultPlanName?: string;
}) {
  const { money } = useCurrency();
  const automations = useMemo(
    () => snapshotAutomations(model, profile, after, horizon),
    [model, profile, after, horizon],
  );

  if (after.basisCells.length === 0) return null;

  const horizonLabel = automationHorizonLabel(horizon);
  const horizonWords = automationHorizonWords(horizon);
  const adjustedCells = Object.keys(profile.staffPlanOverrides || {}).length;
  const markets = profile.scenarioMarkets?.length || 0;

  return (
    <section className="card">
      <div className="card-header">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-brand-purple-600 font-semibold">
            Final Staff Plan
          </div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Flag size={16} className="text-brand-purple-600" />
            Save the plan at {horizonWords}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Writes the matrix above to this client's staff plans, tagged Final,
            along with the scenario inputs that produced it.
          </p>
        </div>
        {/* The horizon lives here as well as above the matrix: this is where the
            choice is committed, and one Final plan is saved per horizon. */}
        <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
          <HorizonTabs horizon={horizon} onChange={onHorizonChange} />
          {onPublishPlan && (
            <PublishPlanButton
              defaultName={`${defaultPlanName || 'Scenario'} — Final (${horizonLabel})`}
              note={
                <>
                  Tagged Final at {horizonWords}. This is post-automation
                  headcount, so it cannot be attached to a scenario as its Actual
                  team — which is what stops automation being released from it
                  twice. Cells automation took to zero are not saved: the plan
                  records the roles that remain.
                </>
              }
              // Scoped to this horizon: a Year 2 and a Full impact plan off the
              // same scenario are two different answers, so re-saving one must
              // not overwrite the other.
              existing={findPublishedPlan?.('final', staffPlanHorizonFor(horizon))}
              onPublish={(name, replaceId) =>
                onPublishPlan({
                  name,
                  kind: 'final',
                  cells: basisCellsToStaffPlanCells(after.cells),
                  source: {
                    horizon: staffPlanHorizonFor(horizon),
                    basis,
                    automationCellCount: after.impactedCells,
                    releasedFte: after.releasedFte,
                    adjustedCellCount: adjustedCells,
                    snapshot: {
                      capturedAt: new Date().toISOString(),
                      modelName: model.name,
                      modelVersion: model.version,
                      profile,
                      automations,
                      beforeFte: after.beforeFte,
                      afterFte: after.afterFte,
                    },
                  },
                  replaceId,
                })
              }
            />
          )}
        </div>
      </div>
      <div className="card-body space-y-3">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <MatrixStat label="Before" value={after.beforeFte} />
          <MatrixStat label="Released" value={after.releasedFte} tone="emerald" />
          <MatrixStat label="After" value={after.afterFte} strong />
          <div className="text-[11px] text-slate-500 max-w-xs">
            {after.impactedCells === 0
              ? 'No automation lands by this point, so the plan would match the pre-automation grid.'
              : `Automation touches ${after.impactedCells} cell${
                  after.impactedCells === 1 ? '' : 's'
                } across ${automations.length} automation${
                  automations.length === 1 ? '' : 's'
                }.`}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Frozen with the plan
          </div>
          <dl className="mt-1.5 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-[11px]">
            <Frozen label="Horizon" value={horizonWords} />
            <Frozen label="Released from" value={basisLabel(basis)} />
            <Frozen
              label="Model"
              value={
                model.version ? `${model.name} v${model.version}` : model.name
              }
            />
            <Frozen label="Budget" value={money(profile.totalBudget || 0)} />
            <Frozen
              label="Markets"
              value={markets ? `${markets} in scope` : 'Not set'}
            />
            <Frozen
              label="Hand adjustments"
              value={
                adjustedCells
                  ? `${adjustedCells} cell${adjustedCells === 1 ? '' : 's'}`
                  : 'None'
              }
            />
          </dl>
          {automations.length > 0 && (
            <div className="mt-2.5 pt-2.5 border-t border-slate-200">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Automations recorded
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {automations.map((a) => (
                  <span
                    key={a.id}
                    className="pill bg-white text-slate-600 border border-slate-200"
                    title={`Starts month ${a.startMonth}, ramps over ${
                      a.rampMonths
                    } months — ${(a.adoption * 100).toFixed(0)}% landed by ${horizonWords}, releasing ${fmtFte(
                      a.releasedFte,
                      2,
                    )} FTE`}
                  >
                    {a.name}
                    <span className="ml-1 text-slate-400 tabular-nums">
                      {fmtFte(a.releasedFte, 2)}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Frozen({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-800 font-medium truncate" title={value}>
        {value}
      </dd>
    </div>
  );
}
